import { useCallback, useEffect, useState } from "react";
import { Timer, Trash2, Info } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiError } from "@/lib/adminApi";

const LABELS = { 0: "Off", 30: "30 days", 60: "60 days", 90: "90 days", 180: "180 days", 365: "1 year" };

const fmt = (iso) =>
  new Date(iso).toLocaleString("en-US", { timeZone: "America/Phoenix", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export const RetentionPanel = ({ onAuthError }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const guard = useCallback((err) => (err?.response?.status === 401 ? onAuthError() : toast.error(apiError(err))), [onAuthError]);

  useEffect(() => { adminApi.get("/admin/retention").then((r) => setData(r.data)).catch(guard); }, [guard]);

  const choose = async (days) => {
    setBusy(true);
    try {
      const { data: d } = await adminApi.put("/admin/retention", { days });
      setData(d);
      toast.success(days ? `Handled inquiries will be deleted ${LABELS[days]} after being marked handled.` : "Automatic deletion is off.");
    } catch (err) { guard(err); } finally { setBusy(false); }
  };

  const runNow = async () => {
    if (!data?.eligible_now) return;
    if (!window.confirm(`Permanently delete ${data.eligible_now} handled inquir${data.eligible_now === 1 ? "y" : "ies"} now? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data: r } = await adminApi.post("/admin/retention/run");
      toast.success(`Deleted ${r.deleted} inquir${r.deleted === 1 ? "y" : "ies"}.`);
      setData((await adminApi.get("/admin/retention")).data);
    } catch (err) { guard(err); } finally { setBusy(false); }
  };

  return (
    <section data-testid="admin-retention-panel" className="bg-cream-ivory border border-[#D9CFB8] p-8 lg:p-10">
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8a6b2f] mb-2">Data retention</p>
      <h2 className="font-serif text-3xl text-navy flex items-center gap-3"><Timer size={22} className="text-[#8a6b2f]" /> Auto-delete handled inquiries</h2>
      <p className="text-ink-muted text-sm mt-3 max-w-xl">
        Inquiries you've marked <strong className="text-navy font-medium">handled</strong> are permanently deleted from the website database this long after being marked. Open (unhandled) inquiries are never deleted. Runs nightly at 3:00 AM Arizona time.
      </p>

      <div className="flex flex-wrap gap-2 mt-8" role="radiogroup" aria-label="Retention period">
        {data?.choices?.map((d) => (
          <button key={d} role="radio" aria-checked={data.days === d} disabled={busy} onClick={() => choose(d)} data-testid={`retention-choice-${d}`}
            className={`font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors disabled:opacity-60 ${data.days === d ? "bg-navy text-parchment border-navy" : "border-[#C9BFA8] text-ink-muted hover:text-navy"}`}>
            {LABELS[d]}
          </button>
        ))}
      </div>

      {data && (
        <div className="mt-8 grid sm:grid-cols-2 gap-6">
          <div className="border border-[#D9CFB8] p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-ink-muted">Past the window right now</p>
            <p data-testid="retention-eligible-count" className="font-serif text-4xl text-navy mt-2">{data.eligible_now}</p>
            <p className="text-ink-muted text-xs mt-1">{data.days ? "Will be removed at the next nightly run." : "Nothing is deleted while this is off."}</p>
            {data.days > 0 && data.eligible_now > 0 && (
              <button onClick={runNow} disabled={busy} data-testid="retention-run-now-btn"
                className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border border-[#C9BFA8] text-ink-muted hover:text-red-700 hover:border-red-700 transition-colors disabled:opacity-60">
                <Trash2 size={12} /> Delete these now
              </button>
            )}
          </div>
          <div className="border border-[#D9CFB8] p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-ink-muted">Last run</p>
            <p data-testid="retention-last-run" className="text-navy mt-2 text-sm">
              {data.last_run ? <>{fmt(data.last_run.created_at)} · {data.last_run.deleted ?? 0} deleted</> : "Not yet run"}
            </p>
          </div>
        </div>
      )}

      <p data-testid="retention-disclaimer" className="flex items-start gap-2 mt-8 text-xs text-ink-muted leading-relaxed max-w-2xl">
        <Info size={14} className="shrink-0 mt-0.5 text-[#8a6b2f]" />
        <span>Deletion removes the record from the website database only. It does not remove the email copies already sent to your inbox (notification and daily digest), the CSV files you've downloaded, or copies held in your hosting provider's database backups, which are retained on their own schedule.</span>
      </p>
    </section>
  );
};
